import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '~/components/elements/Button'
import { Divider } from '~/components/elements/Divider'
import { FavoriteButtonForPc } from '~/components/elements/FavoriteButtonForPc'
import { LikeButtonForPc } from '~/components/elements/LikeButtonForPc'
import { CommentIcon } from '~/components/icons/generated/CommentIcon'
import { AppLeadingDialog } from '~/components/modules/HalfModal/AppLeadingDialog'
import { HalfModal } from '~/components/modules/HalfModal/HalfModal'
import { MemberHeader } from '~/components/modules/Header/MemberHeader/MemberHeader'
import { MemberHeaderForPc } from '~/components/modules/Header/MemberHeader/MemberHeaderForPc'
import { DeprecatedFollowButton } from '~/components/modules/Header/MemberHeader/components/DeprecatedFollowButton'
import { HeadlineForPc } from '~/components/modules/HeadlineForPc/HeadlineForPc'
import { MemberFollow } from '~/components/modules/MemberFollow'
import { MoreHorizPopup } from '~/components/modules/MoreHorizPopup/MoreHorizPopup'
import { ShareButtons } from '~/components/modules/ShareButtons'
import { SpamCategories } from '~/components/modules/SpamModal/SpamCategories'
import { getDynamicLinkByCoordinateId } from '~/features/CoordinateDetail/lib/getDynamicLinkByCoordinateId'
import { CoordinateDetailProps } from '~/features/CoordinateDetail/types'
import { useIsLoggedIn } from '~/hooks/useIsLoggedIn'
import { useIsLoginMember } from '~/hooks/useIsLoginMember'
import { useMainHeaderShadowVisibilityForPcActions } from '~/hooks/useMainHeaderShadowVisibilityForPc'
import { useIsDesktop } from '~/hooks/useMediaQuery'
import { useMember } from '~/hooks/useMember'
import { useModal } from '~/hooks/useModal'
import { useSessionStorage } from '~/hooks/useSessionStorage'
import { useSubHeaderFloating } from '~/hooks/useSubHeaderFloating'
import { COORDINATE_DETAIL_HALF_MODAL_ADJUST_URL } from '~/lib/constants'
import { isReferrerSameHost } from '~/lib/isReferrerSameHost'
import { openPopup } from '~/lib/openPopup'
import { CommentForPc } from './components/CommentForPc'
import { CoordinateHeaderPopup } from './components/CoordinateHeaderPopup'
import { CoordinateImages } from './components/CoordinateImages/CoordinateImages'
import { CoordinateInformation } from './components/CoordinateInformation/CoordinateInformation'
import { CoordinateSubInformation } from './components/CoordinateInformation/CoordinateSubInformation'
import { PopularityCoordinate } from './components/PopularityCoordinate'
import { RecentMemberCoordinate } from './components/RecentMemberCoordinate'
import { UseItems } from './components/UseItems'
import { pcCoordinateDetailAds, spCoordinateDetailAds } from './data/googleAd'
import { useCoordinateDetailShareUrl } from './hooks/useCoordinateDetailShareUrl'
import { getTitle } from './lib/getTitle'
import {
  sendGtmEvAppRedirectHalfModalButton,
  sendGtmEvShowHalfModal,
} from './lib/gtmEvent'

const AppDownloadDialog = dynamic(
  () =>
    import('~/components/modules/AppDownloadDialogForPc').then(
      (mod) => mod.AppDownloadDialog
    ),
  {
    ssr: false,
  }
)

const ThinBannerForSP = dynamic(
  () => import('~/features/shared/banner/SP/ThinBanner'),
  {
    ssr: false,
  }
)

const ThinBannerForPC = dynamic(
  () => import('~/features/shared/banner/PC/ThinBanner'),
  {
    ssr: false,
  }
)

const GoogleAd = dynamic(
  () => import('~/features/shared/ad/components/GoogleAd'),
  {
    ssr: false,
  }
)

const SpamModal = dynamic(
  () =>
    import('~/components/modules/SpamModal/SpamModal').then((mod) => ({
      default: mod.SpamModal,
    })),
  {
    ssr: false,
  }
)

/* eslint-disable max-statements */
/* eslint-disable complexity */
export const CoordinateDetail: React.FC<CoordinateDetailProps> = ({
  coordinate,
  coordinateItems,
  coordinateFolders,
}) => {
  const { subHeaderRef: pcSubHeaderRef, floating: pcSubHeaderFloating } =
    useSubHeaderFloating()
  const { showPcShadow, hidePcShadow } =
    useMainHeaderShadowVisibilityForPcActions()
  const isLoggedIn = useIsLoggedIn()
  const pathUsername = useRouter().query.username
  const isLoginMemberContent = useIsLoginMember(
    typeof pathUsername === 'string' ? pathUsername : ''
  )
  const [isScrolled, setIsScrolled] = useState(false)
  const isDesktop = useIsDesktop()

  const snsShareUrls = useCoordinateDetailShareUrl(
    coordinate.id,
    coordinate.member.user_name
  )

  useEffect(() => {
    if (isScrolled === true) return
    const handleScroll = () => setIsScrolled(true)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isScrolled])

  if (pcSubHeaderFloating) {
    hidePcShadow()
  } else {
    showPcShadow()
  }

  //ハーフモーダル表示のロジック
  const {
    value: coordinateDetailViewCount,
    set: setCoordinateDetailViewCount,
    isReady: isReadySessionStorage,
  } = useSessionStorage(
    'view_count_app_redirect_half_modal_on_coordinate_detail'
  )
  const [isHalfModalOpen, setIsHalfModalOpen] = useState(false)
  const closeHalfModal = useCallback(() => setIsHalfModalOpen(false), [])
  const [viewCountIncremented, setViewCountIncremented] = useState(false)
  if (
    !viewCountIncremented &&
    isReadySessionStorage &&
    isReferrerSameHost(document) // wearからの遷移のみインクリメント
  ) {
    setViewCountIncremented(true)
    const incrementedCount = coordinateDetailViewCount + 1
    setCoordinateDetailViewCount(incrementedCount)
    if (incrementedCount === 2) setIsHalfModalOpen(true)
  }

  const sentGtmEvShowHalfModalRef = useRef(false)
  if (!isDesktop && isHalfModalOpen && !sentGtmEvShowHalfModalRef.current) {
    sendGtmEvShowHalfModal()
    sentGtmEvShowHalfModalRef.current = true
  }

  const dynamicLinkRef = useRef<Promise<string | undefined>>(undefined)

  const member = useMember(coordinate.member.user_name) ?? {
    ...coordinate.member,
    age: null,
    is_age_published: false,
    often_use_coordinate_item_brands: undefined,
  }
  const title = getTitle({
    nickName: coordinate.member.nick_name,
    shopName: coordinate.member.shop?.name,
    isWearista: coordinate.member.is_wearista,
    itemName: coordinateItems[0]?.item?.name,
    categoryName: coordinateItems[0]?.item?.item_category?.name ?? '',
    childCategoryName: coordinateItems[0]?.item?.item_child_category?.name,
  })

  const [
    AppDownloadModal,
    openAppDownloadModal,
    closeAppDownloadModal,
    isAppDownloadModalOpen,
  ] = useModal(200)
  const [appDownloadDialogText, setAppDownloadDialogText] = useState('')
  const openCommentModal = () => {
    setAppDownloadDialogText('「コメント」をするには\nアプリが必要です')
    openAppDownloadModal()
  }
  const openLikeModal = () => {
    setAppDownloadDialogText('「いいね」をするには\nアプリが必要です')
    openAppDownloadModal()
  }
  const openFavoriteModal = () => {
    setAppDownloadDialogText('「お気に入り」をするには\nアプリが必要です')
    openAppDownloadModal()
  }

  const [ReportModal, openReportModal, closeReportModal, reportModalIsOpen] =
    useModal(300)

  return (
    <>
      <div className="xl:hidden">
        <ThinBannerForSP bannerType="sp_narrow_menu_snap_detail" />
      </div>
      <div className="hidden xl:block">
        <ThinBannerForPC bannerType="pc_gbl_ad_snap_detail" />
      </div>
      <div className="flex justify-center bg-gray-200 py-[10px] xl:hidden">
        <div className="min-h-[100px] min-w-[320px] bg-white">
          <GoogleAd elementId={spCoordinateDetailAds.top} />
        </div>
      </div>
      <div className="xl:hidden">
        <MemberHeader member={coordinate.member} hasBottomShadow={false}>
          <CoordinateHeaderPopup coordinate={coordinate} />
        </MemberHeader>
      </div>
      <div className="hidden xl:block">
        <HeadlineForPc>
          <MoreHorizPopup>
            <div className="py-[7px]">
              <ShareButtons
                twitterShareUrl={snsShareUrls.twitterShareUrl}
                facebookShareUrl={snsShareUrls.facebookShareUrl}
              />
            </div>
            {isLoggedIn && !isLoginMemberContent && (
              <button
                onClick={openReportModal}
                className="w-full border-t border-gray-300 py-3 text-[11px] leading-[1.4]"
                type="button"
              >
                通報
              </button>
            )}
          </MoreHorizPopup>
          <ReportModal
            className={`transition-opacity duration-200 ease-linear ${
              reportModalIsOpen ? 'opacity-100' : 'opacity-0'
            }`}
            overlayClassName={`ease-linear duration-200 transition-opacity bg-black/60 ${
              reportModalIsOpen ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <SpamModal
              type={SpamCategories.Coordinate}
              close={closeReportModal}
              targetId={coordinate.id}
            />
          </ReportModal>
        </HeadlineForPc>
      </div>
      <div
        className="sticky top-[54px] z-contentHeader hidden xl:block"
        ref={pcSubHeaderRef}
      >
        <MemberHeaderForPc
          member={member}
          hasBottomShadow={pcSubHeaderFloating}
          linkUrl={
            coordinate.member ? `/${coordinate.member.user_name}/` : undefined
          }
          linkLabel={
            coordinate.member
              ? `${coordinate.member.nick_name}のページへ`
              : undefined
          }
        >
          {member !== undefined && !isLoginMemberContent && (
            <DeprecatedFollowButton member={member} />
          )}
        </MemberHeaderForPc>
      </div>
      <main className="xl:mx-auto xl:grid xl:w-[990px] xl:grid-cols-[558px_1fr] xl:gap-x-5 xl:pt-5 xl:[grid-template-areas:'main_sub'_'main-bottom_sub']">
        <section className="border-y border-gray-300 xl:h-fit xl:overflow-hidden xl:rounded-t-[3px] xl:border xl:[grid-area:main]">
          <CoordinateImages
            coordinate={coordinate}
            coordinateItems={coordinateItems}
          />
          <div className="hidden justify-between border-t border-gray-300 bg-white px-[23px] py-6 xl:flex">
            <FavoriteButtonForPc
              favoriteCount={coordinate.aggregate?.folder_count ?? 0}
              onButtonClick={openFavoriteModal}
            />
            <div className="flex gap-[6px]">
              <LikeButtonForPc
                likeCount={coordinate.aggregate?.like_count ?? 0}
                onButtonClick={openLikeModal}
              />
              <Button color="outlined" onClick={openCommentModal}>
                <span className="flex min-w-[64px] items-center justify-center gap-[5px] px-2 py-[9px]">
                  <CommentIcon className="h-[21px]" />
                  <span className="text-[15px] font-bold">
                    {coordinate.aggregate?.comment_count ?? 0}
                  </span>
                </span>
              </Button>
            </div>
          </div>
          <div className="hidden xl:block">
            <CommentForPc
              coordinateId={coordinate.id}
              memberId={coordinate.member.id}
              nickName={coordinate.member.nick_name}
              onButtonClick={openCommentModal}
            />
          </div>
        </section>
        <section className="pt-[18px] xl:overflow-hidden xl:pt-0 xl:[grid-area:sub]">
          <div className="flex flex-col xl:flex-col-reverse xl:rounded-[3px] xl:border xl:border-gray-300 xl:bg-white xl:pb-[6px] xl:pl-[23px] xl:pt-7">
            {coordinateItems.length > 0 && (
              <section>
                <div className="hidden pt-6 xl:block">
                  <Divider />
                </div>
                <div className="xl:pt-6">
                  <UseItems
                    coordinateItems={coordinateItems}
                    coordinate={coordinate}
                  />
                </div>
              </section>
            )}
            <section className="xl:pr-[23px]">
              <h1 className="hidden text-[16px] font-bold leading-normal xl:block">
                {title}
              </h1>
              <div className="px-4 pt-[38px] xl:px-0 xl:pt-4">
                <CoordinateInformation coordinate={coordinate} />
              </div>
            </section>
          </div>
          <div className="hidden xl:block xl:pt-[18px] xl:text-center">
            <GoogleAd elementId={pcCoordinateDetailAds.sub} />
          </div>
          <CoordinateSubInformation
            coordinateItems={coordinateItems}
            coordinate={coordinate}
            coordinateFolders={coordinateFolders}
            frequentlyUsedBrands={member.often_use_coordinate_item_brands}
          />
        </section>
        <div className="xl:hidden">
          {!isLoginMemberContent ? (
            <div className="bg-gray-200 py-2">
              <MemberFollow member={coordinate.member} />
            </div>
          ) : (
            <div className="bg-gray-200 py-1" />
          )}
        </div>
        <div className="pt-[22px] xl:pt-6 xl:[grid-area:main-bottom]">
          <div className="xl:pb-[10px]">
            <RecentMemberCoordinate
              coordinateId={coordinate.id}
              member={coordinate.member}
            />
          </div>
          {!isLoginMemberContent && (
            <div className="pt-4 xl:pt-0">
              <PopularityCoordinate />
            </div>
          )}
          <div className="hidden xl:block xl:pt-3 xl:text-center">
            <GoogleAd elementId={pcCoordinateDetailAds.mainBottom} />
          </div>
        </div>
        <div className="flex flex-col gap-10 pt-4 xl:hidden">
          <GoogleAd elementId={spCoordinateDetailAds.footer} />
        </div>
      </main>
      <HalfModal
        isOpen={isHalfModalOpen && !isDesktop}
        onClose={closeHalfModal}
      >
        <AppLeadingDialog
          title={'アプリで\n気になるコーディネートを保存しよう'}
          message={
            'アプリならコーディネートを保存して\nいつでも見返すことができます。'
          }
          buttonText="アプリで開く"
          onButtonClick={async () => {
            sendGtmEvAppRedirectHalfModalButton()
            if (dynamicLinkRef.current === undefined)
              dynamicLinkRef.current = getDynamicLinkByCoordinateId(
                coordinate.id,
                COORDINATE_DETAIL_HALF_MODAL_ADJUST_URL
              )
            openPopup(
              dynamicLinkRef.current,
              COORDINATE_DETAIL_HALF_MODAL_ADJUST_URL
            )
          }}
          onCloseClick={closeHalfModal}
        />
      </HalfModal>
      <AppDownloadModal
        wrapperClassName={`ease-linear duration-200 transition-all ${
          isAppDownloadModalOpen ? 'opacity-100' : 'opacity-0'
        }`}
        overlayClassName="bg-black/60"
      >
        <AppDownloadDialog
          close={closeAppDownloadModal}
          text={appDownloadDialogText}
          imageType="coordinateDetail"
        />
      </AppDownloadModal>
    </>
  )
}
